/**
 * spriteMap.ts
 * Maps battle units and tile types to local CC0 pixel-art sprite paths.
 * All sprites come from the Dungeon Crawl Stone Soup tileset (CC0 license),
 * plus a small curated fallback set under sprites/units/.
 *
 * Priority for unit sprites:
 *   monster.imageUrl (local path only - remote/CDN URLs are ignored)
 *   → named monster override (monster.name / monster.family)
 *   → monster.type category
 *   → player characterClass
 *   → fallback default silhouette
 */

import { BattleUnitState, BattleTileState, UnitEquipment } from '../types/battle';

const BASE = process.env.PUBLIC_URL || '';

/** Builds a URL into the Stone Soup monster sprite tree, percent-encoding each
 * path segment (the folder name contains spaces). */
function ss(relativePath: string): string {
  const encoded = relativePath.split('/').map(encodeURIComponent).join('/');
  return `${BASE}/sprites/${encodeURIComponent('Dungeon Crawl Stone Soup Full')}/monster/${encoded}`;
}

// ─── Player class sprites ─────────────────────────────────────────────────────

const PLAYER_CLASS_SPRITES: Record<string, string> = {
  warrior: `${BASE}/sprites/units/players/warrior.png`,
  mage:    `${BASE}/sprites/units/players/mage.png`,
  ranger:  `${BASE}/sprites/units/players/ranger.png`,
  rogue:   `${BASE}/sprites/units/players/rogue.png`,
  cleric:  `${BASE}/sprites/units/players/cleric.png`,
};

/** Builds a URL into the Stone Soup player sprite tree (paper-doll layers). */
function sp(relativePath: string): string {
  const encoded = relativePath.split('/').map(encodeURIComponent).join('/');
  return `${BASE}/sprites/${encodeURIComponent('Dungeon Crawl Stone Soup Full')}/player/${encoded}`;
}

// ─── Player appearance: layered paper-doll (base body + equipment + beard + hair) ──
// Base body is derived from Race + Gender; hair/beard are independent choices
// stored as full sprite paths on the character. Equipment overlay layers (GH-41)
// come from each equipped item's admin-assigned Recipe.EquipSpritePath.

const RACE_BASE_KEY: Record<string, string> = {
  human:    'human',
  elf:      'elf',
  dwarf:    'dwarf',
  orc:      'orc',
  halfling: 'halfling',
};

/** CSS filter presets standing in for skin tone - the tileset ships no native skin-tone variants. */
export const SKIN_TONE_FILTERS: Record<string, string> = {
  default: 'none',
  pale:    'brightness(1.18) saturate(0.85)',
  tan:     'sepia(0.25) saturate(1.35) brightness(0.97)',
  brown:   'sepia(0.45) saturate(1.6) brightness(0.78) hue-rotate(-8deg)',
  dark:    'brightness(0.55) saturate(1.15) contrast(1.1)',
};

export interface PlayerSpriteLayer {
  url: string;
  filter?: string;
}

/** Resolves the default race+gender base body sprite (used both for battle render and the appearance editor preview). */
export function getPlayerBaseSprite(race: string | undefined, gender: string | undefined): string {
  const raceKey = RACE_BASE_KEY[(race ?? '').toLowerCase()] ?? 'human';
  const genderKey = (gender ?? 'Male').toLowerCase() === 'female' ? 'female' : 'male';
  return sp(`base/${raceKey}_${genderKey}.png`);
}

/** An equipped item's slot data, as sent by BattleStateService - only the sprite path matters here. */
interface EquipSpriteSource {
  equipSpritePath?: string | null;
}

/** Resolves a single equipped item to a paper-doll layer, ignoring unset or remote (non-local) sprite paths. */
function equipLayer(item: EquipSpriteSource | undefined): PlayerSpriteLayer | null {
  if (!item?.equipSpritePath || /^https?:\/\//i.test(item.equipSpritePath)) return null;
  return { url: item.equipSpritePath };
}

/**
 * Returns the ordered, stacked sprite layers for a player unit's appearance:
 * cloak -> base body (tinted) -> boots -> legs -> chest armor -> gloves ->
 * shield -> weapon -> beard -> hair -> helm.
 * Equipment layers are omitted for slots with nothing equipped or no
 * admin-assigned EquipSpritePath.
 */
export function getPlayerAppearanceLayers(
  character: {
    race?: string;
    gender?: string;
    skinTone?: string;
    baseSprite?: string | null;
    hairSprite?: string | null;
    beardSprite?: string | null;
  },
  equipment?: UnitEquipment
): PlayerSpriteLayer[] {
  const baseUrl = character.baseSprite || getPlayerBaseSprite(character.race, character.gender);
  const layers: PlayerSpriteLayer[] = [];

  const cloak = equipLayer(equipment?.back);
  if (cloak) layers.push(cloak);

  layers.push({ url: baseUrl, filter: SKIN_TONE_FILTERS[character.skinTone ?? 'default'] ?? undefined });

  const boots = equipLayer(equipment?.feet);
  if (boots) layers.push(boots);

  const legs = equipLayer(equipment?.legs);
  if (legs) layers.push(legs);

  const chest = equipLayer(equipment?.chest);
  if (chest) layers.push(chest);

  const gloves = equipLayer(equipment?.hands);
  if (gloves) layers.push(gloves);

  const shield = equipLayer(equipment?.offHand);
  if (shield) layers.push(shield);

  const weapon = equipLayer(equipment?.weapon);
  if (weapon) layers.push(weapon);

  if (character.beardSprite) layers.push({ url: character.beardSprite });
  if (character.hairSprite) layers.push({ url: character.hairSprite });

  const helm = equipLayer(equipment?.head);
  if (helm) layers.push(helm);

  return layers;
}

// ─── Monster type sprites (by monster.type enum string) ───────────────────────

const MONSTER_TYPE_SPRITES: Record<string, string> = {
  beast:       ss('animals/wolf.png'),
  undead:      ss('undead/lich.png'),
  elemental:   ss('nonliving/fire_elemental_new.png'),
  humanoid:    ss('orc_new.png'),
  dragon:      ss('dragons/dragon.png'),
  construct:   ss('nonliving/stone_golem.png'),
  plant:       ss('fungi_plants/treant.png'),
  fiend:       ss('demons/pit_fiend.png'),
  celestial:   ss('holy/angel_new.png'),
  aberration:  ss('aberration/unseen_horror_new.png'),
};

// ─── Named / family monster overrides (lower-case match on name or family) ────
// Keys are matched as substrings against monster.name / monster.family, so
// generic monster vocabulary (goblin, dragon, zombie...) catches most
// Dofus-imported names. Longer/more specific keys should stay above shorter
// ones that could accidentally match a substring of them.

const MONSTER_NAME_SPRITES: Record<string, string> = {
  // Dragons & draconic
  'ice dragon':    ss('dragons/ice_dragon_new.png'),
  'golden dragon': ss('dragons/golden_dragon.png'),
  'shadow dragon': ss('dragons/shadow_dragon.png'),
  'storm dragon':  ss('dragons/storm_dragon_new.png'),
  'bone dragon':   ss('undead/bone_dragon_new.png'),
  dragon:          ss('dragons/dragon.png'),
  wyvern:          ss('dragons/wyvern_new.png'),
  hydra:           ss('dragons/hydra_1_new.png'),
  drake:           ss('draconic/draconic_base-green_new.png'),

  // Undead
  lich:            ss('undead/lich.png'),
  vampire:         ss('undead/vampire_new.png'),
  mummy:           ss('undead/mummy.png'),
  ghoul:           ss('undead/ghoul.png'),
  ghost:           ss('undead/ghost_new.png'),
  wraith:          ss('undead/wraith.png'),
  wight:           ss('undead/wight_new.png'),
  revenant:        ss('undead/revenant.png'),
  skeleton:        ss('undead/skeletons/skeleton_humanoid_small_new.png'),
  zombie:          ss('undead/zombies/zombie_small.png'),

  // Humanoids
  goblin:          ss('goblin_new.png'),
  hobgoblin:       ss('hobgoblin_new.png'),
  orc:             ss('orc_new.png'),
  ogre:            ss('ogre_new.png'),
  troll:           ss('troll.png'),
  minotaur:        ss('minotaur.png'),
  centaur:         ss('centaur.png'),
  satyr:           ss('satyr.png'),
  sphinx:          ss('sphinx_new.png'),
  cyclops:         ss('cyclops_new.png'),
  gnoll:           ss('gnoll_new.png'),
  kobold:          ss('kobold_new.png'),
  dwarf:           ss('dwarf_new.png'),
  elf:             ss('elf_new.png'),
  necromancer:     ss('necromancer_new.png'),
  wizard:          ss('wizard.png'),
  'death knight':  ss('death_knight.png'),
  titan:           ss('titan_new.png'),
  merfolk:         ss('merfolk.png'),
  mermaid:         ss('mermaid.png'),
  gnome:           ss('gnome.png'),
  halfling:        ss('halfling_new.png'),
  human:           ss('human_new.png'),

  // Beasts
  wolf:            ss('animals/wolf.png'),
  bear:            ss('animals/bear.png'),
  spider:          ss('animals/spider.png'),
  snake:           ss('animals/snake.png'),
  rat:             ss('animals/rat.png'),
  scorpion:        ss('animals/scorpion_new.png'),
  toad:            ss('animals/giant_toad.png'),
  frog:            ss('animals/giant_frog.png'),
  basilisk:        ss('animals/basilisk.png'),
  crocodile:       ss('animals/crocodile.png'),
  alligator:       ss('animals/alligator.png'),
  elephant:        ss('animals/elephant_new.png'),
  hound:           ss('animals/hound.png'),
  jackal:          ss('animals/jackal_new.png'),
  warg:            ss('animals/warg.png'),
  viper:           ss('animals/viper.png'),
  turtle:          ss('animals/turtle.png'),
  centipede:       ss('animals/giant_centipede.png'),
  bee:             ss('animals/bumblebee.png'),

  // Demons / fiends
  imp:             ss('demons/imp.png'),
  devil:           ss('demons/red_devil_new.png'),
  efreet:          ss('demons/efreet.png'),
  cacodemon:       ss('demons/cacodemon.png'),
  demon:           ss('demons/pit_fiend.png'),

  // Celestial / holy
  angel:           ss('holy/angel_new.png'),
  paladin:         ss('holy/paladin.png'),
  phoenix:         ss('phoenix.png'),

  // Constructs / elementals
  golem:           ss('nonliving/stone_golem.png'),
  gargoyle:        ss('nonliving/gargoyle.png'),
  'fire elemental':  ss('nonliving/fire_elemental_new.png'),
  'water elemental': ss('nonliving/water_elemental_new.png'),
  'air elemental':   ss('nonliving/air_elemental_new.png'),
  'earth elemental': ss('nonliving/earth_elemental.png'),
  elemental:       ss('nonliving/fire_elemental_new.png'),

  // Plants / oozes
  treant:          ss('fungi_plants/treant.png'),
  mushroom:        ss('fungi_plants/deathcap.png'),
  plant:           ss('fungi_plants/oklob_plant.png'),
  ooze:            ss('amorphous/ooze_new.png'),
  jelly:           ss('amorphous/jelly.png'),
  slime:           ss('amorphous/acid_blob.png'),

  // Aquatic
  shark:           ss('aquatic/shark_new.png'),
  kraken:          ss('aquatic/kraken_head_new.png'),

  // Aberrations / eyes
  eyeball:         ss('eyes/giant_eyeball.png'),
  'beholder':      ss('eyes/great_orb_of_eyes.png'),

  // Other named creatures
  harpy:           ss('harpy.png'),
  griffon:         ss('griffon.png'),
  naga:            ss('naga.png'),
  manticore:       ss('manticore.png'),
  bat:             ss('vault/megabat.png'),

  // Generic size-modifier fallback - kept last so specific creature words
  // (rat, spider, toad...) win the match for "Giant Rat"-style names first.
  giant:           ss('hill_giant_new.png'),
};

const DEFAULT_MONSTER_SPRITE = `${BASE}/sprites/units/monsters/default.png`;
const DEFAULT_PLAYER_SPRITE  = `${BASE}/sprites/units/players/default.png`;

// ─── Terrain tile sprites ─────────────────────────────────────────────────────

const TERRAIN_SPRITES: Record<string, string> = {
  normal:     `${BASE}/sprites/terrain/normal.png`,
  grass:      `${BASE}/sprites/terrain/grass.png`,
  forest:     `${BASE}/sprites/terrain/forest.png`,
  mountain:   `${BASE}/sprites/terrain/mountain.png`,
  water:      `${BASE}/sprites/terrain/water.png`,
  deepwater:  `${BASE}/sprites/terrain/deepwater.png`,
  sand:       `${BASE}/sprites/terrain/sand.png`,
  swamp:      `${BASE}/sprites/terrain/swamp.png`,
  ice:        `${BASE}/sprites/terrain/ice.png`,
  lava:       `${BASE}/sprites/terrain/lava.png`,
  road:       `${BASE}/sprites/terrain/road.png`,
  rock:       `${BASE}/sprites/terrain/rock.png`,
  highground: `${BASE}/sprites/terrain/highground.png`,
  difficult:  `${BASE}/sprites/terrain/difficult.png`,
  rubble:     `${BASE}/sprites/terrain/rubble.png`,
};

// ─── FX / projectile sprites ──────────────────────────────────────────────────

const FX_SPRITES: Record<string, string> = {
  arrow:     `${BASE}/sprites/fx/arrow.png`,
  magic:     `${BASE}/sprites/fx/magic.png`,
  fireball:  `${BASE}/sprites/fx/fireball.png`,
  heal:      `${BASE}/sprites/fx/heal.png`,
  slash:     `${BASE}/sprites/fx/slash.png`,
  impact:    `${BASE}/sprites/fx/impact.png`,
  ice:       `${BASE}/sprites/fx/ice.png`,
  lightning: `${BASE}/sprites/fx/lightning.png`,
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the local sprite URL for a battle unit.
 * Falls back through: admin-picked local imageUrl → named override → type → class → default.
 */
export function getUnitSprite(unit: BattleUnitState): string {
  // 1. Admin-picked local monster imageUrl (e.g. "/sprites/..."). Remote/CDN
  // URLs (legacy Dofus imports) are intentionally ignored - see spriteMap.ts docblock.
  if (unit.monster?.imageUrl && !/^https?:\/\//i.test(unit.monster.imageUrl)) {
    return unit.monster.imageUrl;
  }

  // 2. Named / family override (monsters)
  if (!unit.isPlayerUnit) {
    const nameLower  = (unit.monster?.name  ?? unit.name ?? '').toLowerCase();
    const familyLower = ((unit.monster as Record<string, unknown>)?.['family'] as string ?? '').toLowerCase();

    for (const key of Object.keys(MONSTER_NAME_SPRITES)) {
      if (nameLower.includes(key) || familyLower.includes(key)) {
        return MONSTER_NAME_SPRITES[key];
      }
    }

    // 3. Type-based fallback
    const monType = unit.monster?.type as string | undefined;
    const typeLower = (monType ?? unit.enemyType ?? '').toLowerCase();
    if (MONSTER_TYPE_SPRITES[typeLower]) return MONSTER_TYPE_SPRITES[typeLower];

    return DEFAULT_MONSTER_SPRITE;
  }

  // 4. Player class sprite
  const classLower = (unit.characterClass ?? '').toLowerCase();
  if (PLAYER_CLASS_SPRITES[classLower]) return PLAYER_CLASS_SPRITES[classLower];

  return DEFAULT_PLAYER_SPRITE;
}

/**
 * Returns the ordered sprite layers to render for a battle unit.
 * Monsters (and players with no appearance data yet) render as a single layer
 * via getUnitSprite(); players with appearance data render the layered
 * paper-doll (base body + equipment + beard + hair) via getPlayerAppearanceLayers().
 */
export function getUnitSpriteLayers(unit: BattleUnitState): PlayerSpriteLayer[] {
  if (unit.isPlayerUnit && unit.character?.gender) {
    return getPlayerAppearanceLayers(unit.character, unit.equipment);
  }
  return [{ url: getUnitSprite(unit) }];
}

/**
 * Returns the local terrain sprite URL for a tile, or null if unmapped.
 * When null, the caller should fall back to the CSS background colour.
 */
export function getTerrainSprite(tile: BattleTileState): string | null {
  const typeLower = (tile.type ?? '').toLowerCase().replace(/\s/g, '');
  return TERRAIN_SPRITES[typeLower] ?? null;
}

/**
 * Returns the FX sprite URL for a given skill or action name.
 */
export function getFxSprite(skillOrAction: string): string {
  const lower = (skillOrAction ?? '').toLowerCase();
  if (lower.includes('fireball') || lower.includes('fire'))  return FX_SPRITES.fireball;
  if (lower.includes('ice') || lower.includes('frost') || lower.includes('cold')) return FX_SPRITES.ice;
  if (lower.includes('lightning') || lower.includes('bolt') || lower.includes('thunder')) return FX_SPRITES.lightning;
  if (lower.includes('heal') || lower.includes('restore') || lower.includes('cure')) return FX_SPRITES.heal;
  if (lower.includes('arrow') || lower.includes('shot') || lower.includes('ranged')) return FX_SPRITES.arrow;
  if (lower.includes('magic') || lower.includes('spell') || lower.includes('arcane') || lower.includes('missile')) return FX_SPRITES.magic;
  return FX_SPRITES.slash;
}
